import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Download, Share, X, Smartphone } from "lucide-react";
import { cn } from "@/lib/utils";

/**
 * PWAInstallPrompt
 * - Bandeau discret en bas d'écran invitant à installer Lyko.
 * - Détecte l'événement `beforeinstallprompt` (Chrome / Edge / Android).
 * - Affiche un guide manuel pour iOS / Safari (pas d'API native).
 * - Ne s'affiche jamais si l'app est déjà installée (display-mode: standalone)
 *   ni si l'utilisateur a fermé le bandeau (mémorisé en localStorage).
 * - Aucun service worker — purement additif, ne touche pas à l'auth ni au QR.
 */

type BeforeInstallPromptEvent = Event & {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: "accepted" | "dismissed" }>;
};

const DISMISS_KEY = "lyko_pwa_install_dismissed_at";
const DISMISS_DAYS = 14; // ne pas reproposer avant 14 jours après fermeture

// Routes sur lesquelles le bandeau ne doit JAMAIS apparaître
// (parcours QR, check-in/out, scan fournisseur, set-password, activation…).
const EXCLUDED_PATH_PREFIXES = [
  "/scan",
  "/s/", // entity_access_links tokenisés
  "/checkin",
  "/visitor-checkin",
  "/centrale-visitor-checkin",
  "/qr-supplier",
  "/qr-dashboard",
  "/set-password",
  "/auth",
];

function isExcludedPath(pathname: string): boolean {
  return EXCLUDED_PATH_PREFIXES.some((p) => pathname === p || pathname.startsWith(p + "/") || pathname.startsWith(p));
}

function isStandalone(): boolean {
  if (typeof window === "undefined") return false;
  const mq = window.matchMedia?.("(display-mode: standalone)").matches;
  // iOS Safari
  // @ts-expect-error - propriété non standard
  const iosStandalone = window.navigator.standalone === true;
  return Boolean(mq || iosStandalone);
}

function isIOS(): boolean {
  if (typeof navigator === "undefined") return false;
  const ua = navigator.userAgent || "";
  return /iPad|iPhone|iPod/.test(ua) && !/CriOS|FxiOS|EdgiOS/.test(ua);
}

function wasRecentlyDismissed(): boolean {
  try {
    const raw = localStorage.getItem(DISMISS_KEY);
    if (!raw) return false;
    const ts = Number(raw);
    if (!Number.isFinite(ts)) return false;
    const ageDays = (Date.now() - ts) / (1000 * 60 * 60 * 24);
    return ageDays < DISMISS_DAYS;
  } catch {
    return false;
  }
}

export function PWAInstallPrompt() {
  const [deferred, setDeferred] = useState<BeforeInstallPromptEvent | null>(null);
  const [visible, setVisible] = useState(false);
  const [showIOSHelp, setShowIOSHelp] = useState(false);
  const ios = isIOS();
  const [pathname, setPathname] = useState<string>(
    typeof window !== "undefined" ? window.location.pathname : "/"
  );

  useEffect(() => {
    if (typeof window === "undefined") return;
    const onPop = () => setPathname(window.location.pathname);
    window.addEventListener("popstate", onPop);
    // Patch léger pour détecter les navigations SPA (push/replaceState)
    const origPush = window.history.pushState;
    const origReplace = window.history.replaceState;
    window.history.pushState = function (...args) {
      origPush.apply(this, args as never);
      setPathname(window.location.pathname);
    };
    window.history.replaceState = function (...args) {
      origReplace.apply(this, args as never);
      setPathname(window.location.pathname);
    };
    return () => {
      window.removeEventListener("popstate", onPop);
      window.history.pushState = origPush;
      window.history.replaceState = origReplace;
    };
  }, []);

  useEffect(() => {
    if (isStandalone() || wasRecentlyDismissed()) return;

    const handler = (e: Event) => {
      e.preventDefault();
      setDeferred(e as BeforeInstallPromptEvent);
      setVisible(true);
    };
    window.addEventListener("beforeinstallprompt", handler);

    // Sur iOS, pas d'événement : afficher après quelques secondes si pertinent
    let iosTimer: number | undefined;
    if (ios) {
      iosTimer = window.setTimeout(() => setVisible(true), 4000);
    }

    const installedHandler = () => {
      setVisible(false);
      setDeferred(null);
    };
    window.addEventListener("appinstalled", installedHandler);

    return () => {
      window.removeEventListener("beforeinstallprompt", handler);
      window.removeEventListener("appinstalled", installedHandler);
      if (iosTimer) window.clearTimeout(iosTimer);
    };
  }, [ios]);

  const dismiss = () => {
    try {
      localStorage.setItem(DISMISS_KEY, String(Date.now()));
    } catch {
      /* noop */
    }
    setVisible(false);
    setShowIOSHelp(false);
  };

  const install = async () => {
    if (deferred) {
      try {
        await deferred.prompt();
        await deferred.userChoice;
      } catch {
        /* noop */
      }
      setDeferred(null);
      setVisible(false);
      return;
    }
    if (ios) {
      setShowIOSHelp(true);
    }
  };

  if (!visible) return null;
  if (isExcludedPath(pathname)) return null;

  return (
    <div
      role="dialog"
      aria-label="Installer Lyko"
      className={cn(
        "fixed inset-x-0 bottom-0 z-[60] px-3 pb-[max(env(safe-area-inset-bottom),12px)] pt-2",
        "pointer-events-none"
      )}
    >
      <div
        className={cn(
          "pointer-events-auto mx-auto max-w-md rounded-2xl border border-border bg-background shadow-lg",
          "p-3 sm:p-4"
        )}
      >
        {!showIOSHelp ? (
          <div className="flex items-start gap-3">
            <div
              className="shrink-0 rounded-xl p-2"
              style={{ background: "linear-gradient(135deg, #2F80ED, #27AE60)" }}
            >
              <Smartphone className="h-5 w-5 text-white" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold text-foreground">
                📲 Installez Lyko sur votre appareil
              </p>
              <p className="mt-0.5 text-xs text-foreground/70">
                Accès rapide en magasin, sans passer par le navigateur.
              </p>
              <div className="mt-2 flex flex-wrap items-center gap-2">
                <Button size="sm" onClick={install} className="gap-2 h-9 px-3">
                  <Download className="h-4 w-4" />
                  Installer Lyko
                </Button>
                <button
                  type="button"
                  onClick={dismiss}
                  className="text-xs text-foreground/60 hover:text-foreground underline-offset-2 hover:underline px-2 py-1"
                >
                  Plus tard
                </button>
              </div>
            </div>
            <button
              type="button"
              onClick={dismiss}
              aria-label="Fermer"
              className="shrink-0 rounded-md p-1 text-foreground/50 hover:text-foreground hover:bg-muted/50"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        ) : (
          <div className="flex items-start gap-3">
            <div className="shrink-0 rounded-xl p-2 bg-muted">
              <Share className="h-5 w-5 text-primary" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold text-foreground">
                Installer Lyko sur iPhone
              </p>
              <ol className="mt-1 list-decimal pl-4 text-xs text-foreground/70 space-y-0.5">
                <li>
                  Touchez l'icône <span className="font-semibold">Partager</span> en bas de Safari.
                </li>
                <li>
                  Choisissez <span className="font-semibold">« Sur l'écran d'accueil »</span>.
                </li>
                <li>Validez en haut à droite.</li>
              </ol>
              <div className="mt-2">
                <button
                  type="button"
                  onClick={dismiss}
                  className="text-xs text-foreground/60 hover:text-foreground underline-offset-2 hover:underline px-2 py-1"
                >
                  J'ai compris
                </button>
              </div>
            </div>
            <button
              type="button"
              onClick={dismiss}
              aria-label="Fermer"
              className="shrink-0 rounded-md p-1 text-foreground/50 hover:text-foreground hover:bg-muted/50"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

export default PWAInstallPrompt;
